import { useCallback, useEffect, useState } from 'react';
import {
  desconectarSesion,
  haceCuanto,
  sesionesDelNegocio,
  type SesionConectada,
} from '../lib/api-sesiones';
import { Boton, Campo, EncabezadoTabla, Esqueleto, FilaTabla, Insignia, MensajeError, TablaAncha, Tarjeta, TextoAyuda } from '../ui/base';

const NOMBRE_ROL: Record<string, string> = { propietario: 'Dueño', cajero: 'Cajero', contador: 'Contador' };

/**
 * Las sesiones activas del negocio, con el botón de desconectar. La propia no se puede desconectar
 * desde aquí (para eso está Salir) y el motivo es obligatorio, como en la anulación.
 */
export function DispositivosConectados({ negocioId }: { negocioId: string }) {
  const [filas, setFilas] = useState<SesionConectada[] | null>(null);
  const [error, setError] = useState('');
  const [aDesconectar, setADesconectar] = useState<SesionConectada | null>(null);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    sesionesDelNegocio(negocioId)
      .then(setFilas)
      .catch((e) => {
        setFilas([]);
        setError(e instanceof Error ? e.message : 'No pudimos leer las sesiones.');
      });
  }, [negocioId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  function desconectar() {
    if (!aDesconectar || !motivo.trim()) return;
    setOcupado(true);
    setError('');
    desconectarSesion(aDesconectar.sesionId, motivo.trim())
      .then(() => {
        setADesconectar(null);
        setMotivo('');
        cargar();
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'No pudimos desconectar la sesión.'))
      .finally(() => setOcupado(false));
  }

  return (
    <Tarjeta>
      <h2 className="mb-2 font-extrabold">Dispositivos conectados</h2>
      <TextoAyuda>
        Las sesiones activas de este negocio. La última renovación ocurre con el uso; al desconectar, el
        dispositivo sale al refrescar o en menos de una hora, cuando su acceso vigente caduque.
      </TextoAyuda>
      <MensajeError>{error}</MensajeError>
      {filas === null ? (
        <Esqueleto lineas={3} />
      ) : filas.length === 0 ? (
        <p className="text-muted text-contenido">Sin sesiones activas.</p>
      ) : (
        <TablaAncha>
          <EncabezadoTabla>
            <th className="py-2 pr-2">Quién</th>
            <th className="py-2 pr-2">Rol</th>
            <th className="py-2 pr-2">Desde</th>
            <th className="py-2 pr-2">Última renovación</th>
            <th className="py-2 pr-2">Dispositivo</th>
            <th className="py-2 pr-2" />
          </EncabezadoTabla>
          <tbody>
            {filas.map((s) => (
              <FilaTabla key={s.sesionId} data-e2e="fila-sesion">
                <td className="py-2 pr-2 font-semibold">{s.nombre ?? '—'}</td>
                <td className="py-2 pr-2 text-contenido">{NOMBRE_ROL[s.rol] ?? s.rol}</td>
                <td className="py-2 pr-2 text-contenido">{new Date(s.creadaEn).toLocaleDateString('es-CO')}</td>
                <td className="py-2 pr-2 text-contenido">{haceCuanto(s.renovadaEn)}</td>
                <td className="text-muted py-2 pr-2 text-etiqueta">{s.dispositivo ?? '—'}</td>
                <td className="py-2 pr-2 text-right">
                  {s.esActual ? (
                    <Insignia tono="neutro">ESTA SESIÓN</Insignia>
                  ) : (
                    <Boton
                      variante="secundario"
                      disabled={ocupado}
                      onClick={() => {
                        setADesconectar(s);
                        setMotivo('');
                      }}
                      data-e2e="sesion-desconectar"
                    >
                      Desconectar
                    </Boton>
                  )}
                </td>
              </FilaTabla>
            ))}
          </tbody>
        </TablaAncha>
      )}
      {aDesconectar && (
        <div className="bg-info/10 rounded-control mt-3 px-3 py-2" data-e2e="sesion-confirmar">
          <p className="mb-2 text-contenido font-semibold">
            Desconectar a {aDesconectar.nombre ?? 'este dispositivo'}: la sesión dejará de renovarse; el
            dispositivo sale al refrescar o en menos de una hora.
          </p>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
            <Campo
              etiqueta="¿Por qué se desconecta?"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
            <div className="flex items-end">
              <Boton variante="secundario" disabled={ocupado} onClick={() => setADesconectar(null)}>
                Volver
              </Boton>
            </div>
            <div className="flex items-end">
              <Boton disabled={ocupado || !motivo.trim()} onClick={desconectar} data-e2e="sesion-confirmar-boton">
                Desconectar
              </Boton>
            </div>
          </div>
        </div>
      )}
    </Tarjeta>
  );
}
